/**
 * Akinex Executive Briefing — frontend entity (Patch 35.5.1).
 *
 * The card is a **deterministic renderer** of the canonical backend
 * response. Every field consumed here must come from
 * ``GET /api/v1/workspaces/{workspace_id}/akinex/briefing``. No
 * client-side ranking, rewriting, or inference is permitted.
 *
 * Founder decisions (accept / dismiss) are POSTed to
 * ``/akinex/briefing/decisions``; the endpoint records intent on the
 * immutable ``akinex_timeline`` ledger only — it never mutates any
 * business record.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { toast } from "sonner";

import { http } from "@/shared/api/http";

// ─── Types (mirror app.akinex.executive.core.py) ─────────────────────
export type AkinexSeverity = "critical" | "high" | "medium" | "low";
export type AkinexConfidence =
  | "very_high"
  | "high"
  | "moderate"
  | "low";

export interface AkinexEvidence {
  facts: Array<Record<string, unknown>>;
  sources: string[];
}

export interface AkinexFinding {
  module: string;
  key: string;
  title: string;
  severity: AkinexSeverity;
  confidence: AkinexConfidence;
  recommended_action: string;
  evidence: AkinexEvidence;
  timestamp: string;
  approvable: boolean;
}

export interface AkinexModuleCatalogEntry {
  name: string;
  status: "active" | "inactive";
}

/** Optional — rendered only when the backend returns it. */
export interface AkinexRecentWin {
  title: string;
  detail?: string;
  timestamp?: string;
  source?: string;
}

export interface AkinexBriefing {
  workspace_id: string;
  generated_at: string;
  headline: string;
  attention_count: number;
  buckets: Record<AkinexSeverity, AkinexFinding[]>;
  recommendations: string[];
  module_catalog: AkinexModuleCatalogEntry[];
  /** Optional — only present when the backend surfaces verified wins. */
  recent_wins?: AkinexRecentWin[];
}

export type AkinexDecision = "accepted" | "dismissed" | "resolved";

export interface AkinexDecisionInput {
  finding_key: string;
  decision: AkinexDecision;
}

// ─── Shared helpers ──────────────────────────────────────────────────
/** Ordered severity — matches the backend's ranking convention. */
export const SEVERITY_ORDER: readonly AkinexSeverity[] = [
  "critical",
  "high",
  "medium",
  "low",
] as const;

/**
 * Flatten the four severity buckets in ranked order. Every bucket is
 * defensively coerced to ``[]`` so a partial or legacy briefing
 * response (e.g. missing the ``low`` key) never crashes render. This
 * function is intentionally the ONE place that walks the buckets — all
 * consumers must go through it so the defensive guard cannot be
 * defeated by a second, unguarded flatten elsewhere.
 */
export function flattenBriefingFindings(
  briefing: AkinexBriefing,
): AkinexFinding[] {
  const out: AkinexFinding[] = [];
  const buckets = briefing.buckets ?? ({} as AkinexBriefing["buckets"]);
  for (const sev of SEVERITY_ORDER) {
    const bucket = buckets[sev] ?? [];
    out.push(...bucket);
  }
  return out;
}

// ─── Query keys ──────────────────────────────────────────────────────
export const akinexKeys = {
  all: (workspaceId: string) => ["akinex", workspaceId] as const,
  briefing: (workspaceId: string) =>
    ["akinex", workspaceId, "briefing"] as const,
};

// ─── API calls ───────────────────────────────────────────────────────
export function fetchAkinexBriefing(
  workspaceId: string,
): Promise<AkinexBriefing> {
  return http<AkinexBriefing>(
    `/workspaces/${workspaceId}/akinex/briefing`,
  );
}

export function recordAkinexDecision(
  workspaceId: string,
  input: AkinexDecisionInput,
): Promise<{ ok: boolean }> {
  return http<{ ok: boolean }>(
    `/workspaces/${workspaceId}/akinex/briefing/decisions`,
    { method: "POST", body: input },
  );
}

// ─── React Query hooks ───────────────────────────────────────────────
export function useAkinexBriefing(
  workspaceId: string | null,
): UseQueryResult<AkinexBriefing, unknown> {
  return useQuery({
    queryKey: workspaceId
      ? akinexKeys.briefing(workspaceId)
      : ["akinex", "no-workspace", "briefing"],
    queryFn: () => fetchAkinexBriefing(workspaceId as string),
    enabled: !!workspaceId,
    // The briefing is expensive to recompute on the backend but its
    // signal degrades quickly — poll gently.
    staleTime: 60_000,
    refetchInterval: 300_000,
    retry: false,
  });
}

export function useAkinexDecision(
  workspaceId: string | null,
): UseMutationResult<
  { ok: boolean },
  unknown,
  AkinexDecisionInput,
  unknown
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AkinexDecisionInput) => {
      if (!workspaceId) {
        return Promise.reject(new Error("No workspace selected."));
      }
      return recordAkinexDecision(workspaceId, input);
    },
    // Mutation-scoped side effects. `onSuccess` fires against the
    // mutation observer inside React Query itself, so it still runs
    // even when the initiating component has unmounted (e.g. Accept
    // triggers navigation and unmounts the card before the POST
    // resolves). Toasts + query invalidation therefore live here —
    // NOT at the ``mutate()`` call site, which is dropped on unmount.
    onSuccess: (_data, variables) => {
      if (!workspaceId) return;
      qc.invalidateQueries({
        queryKey: akinexKeys.briefing(workspaceId),
        // ``refetchType: 'all'`` marks inactive observers stale AND
        // forces a fetch on next mount, so returning to /home shows
        // the refreshed briefing without waiting for staleTime.
        refetchType: "all",
      });
      toast.success(
        variables.decision === "accepted"
          ? "Recorded on the Akinex timeline."
          : "Dismissed. Recorded on the Akinex timeline.",
        { id: `akinex-decision-${variables.finding_key}` },
      );
    },
    onError: () => {
      toast.error("We couldn't record that decision. Please try again.");
    },
  });
}
